import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import validate from '@global/vendors/jquery.validate.min';
import useAlert from '@global/composable/useAlert';
import useAxiosAPI from '@global/composable/api/useAxiosAPI';
import useModalStore from '@global/stores/useModalStore';

/**
 * @description 餐飲的退貨跟別人不一樣
 */
const { alertDialog } = useAlert();
const { getOrderAPI, postRefundCalculateAPI } = useAxiosAPI();

const modalStore = useModalStore();
const { closeModal } = modalStore;

function useRefundList(props) {
  const step = ref(0);
  const data = ref([]);
  const selectedItem = ref([]);
  const selectedItemReason = ref([]);
  const selectedItemType2Reason = ref([]);
  const selectedItemOtherReason = ref([]);
  const dataIsLoad = ref(false);
  const isCheckGift = ref(false);
  const giftData = ref([]);
  const reservedMarkets = ref([]);
  const reservedUnMatchAddons = ref([]);
  const fillReasonIndex = ref(0);
  const selectedItemDetail = ref({});
  const reasonCheck = ref([
    {
      code: 'a',
      check: false,
      text: '一、商品不符所需(尺寸/版型/材質...等)',
      sub: [
        { code: '1', check: false, text: '尺寸過小或過短' },
        { code: '2', check: false, text: '尺寸過大或過長' },
        { code: '3', check: false, text: '材質不符預期' },
        { code: '4', check: false, text: '版型不符預期' },
      ],
    },
    {
      code: 'b',
      check: false,
      text: '二、商品瑕疵問題(破損/污漬/車線...等)',
      sub: [
        { code: '1', check: false, text: '商品有破損' },
        { code: '2', check: false, text: '商品有污漬' },
        { code: '3', check: false, text: '商品有異味' },
        { code: '4', check: false, text: '商品剪裁瑕疵' },
        { code: '5', check: false, text: '車線問題' },
        { code: '6', check: false, text: '商品褪色' },
        {
          code: '7',
          check: false,
          text: '商品功能性配件缺損(如鈕釦/拉鏈...等)',
        },
      ],
    },
    {
      code: 'c',
      check: false,
      text: '三、商品內容物問題',
      sub: [
        { code: '1', check: false, text: '商品缺件' },
        { code: '2', check: false, text: '寄錯商品' },
        { code: '3', check: false, text: '買錯商品' },
      ],
    },
    {
      code: 'd',
      check: false,
      text: '四、商品價格問題',
      sub: [
        {
          code: '1',
          check: false,
          text: '部分退貨後整體未達免運/活動門檻',
        },
        { code: '2', check: false, text: '想參與較晚開始的行銷活動' },
        { code: '3', check: false, text: '下單後發現沒輸入折扣碼' },
      ],
    },
  ]);
  const refundForm = ref(null);

  const type2ReasonOverMax = computed(() => {
    // 當有選到第二大項的原因
    if (data.value && Object.keys(selectedItemDetail.value).length !== 0) {
      return selectedItemType2Reason.value[fillReasonIndex.value].length > 48;
    }
    return false;
  });

  const type5ReasonOverMax = computed(() => {
    // 要判斷五(其他)是否輸入超過48個字元
    if (data.value && Object.keys(selectedItemDetail.value).length !== 0) {
      return selectedItemOtherReason.value[fillReasonIndex.value].length > 48;
    }
    return false;
  });

  const showNextStep = computed(() => {
    const selectItemIndex = selectedItem.value.findIndex(
      (item) => item.OrderMarketID === selectedItemDetail.value.ID,
    );
    // 最後一項商品
    if (selectedItem.value.length === selectItemIndex + 1) {
      return false;
    }
    return true;
  });

  const canMoveNextItem = computed(() => {
    let result = true;
    const reg = /^b+/gm;

    if (selectedItemDetail.value.reasonSelect.length === 0 || type5ReasonOverMax.value) {
      result = false;
      return result;
    }

    // 有勾選主項目但副項目沒勾選的狀況下，不通過
    reasonCheck.value.forEach((category) => {
      if (category.check) {
        if (category.sub.filter((sub) => sub.check).length === 0) {
          result = false;
        }
      }
    });

    selectedItemDetail.value.reasonSelect.forEach((category) => {
      if (reg.test(category)) {
        // 有勾選第二類大項就必須填寫原因
        if (selectedItemType2Reason.value[fillReasonIndex.value].length === 0) {
          result = false;
        }

        // 有勾選第二類大項，字數就不能大於48
        if (type2ReasonOverMax.value) {
          result = false;
        }
      }
    });

    return result;
  });

  const upToTwoSub = computed(() => {
    return reasonCheck.value.find((e) => e.code === 'b').sub.filter((e) => e.check).length === 2;
  });

  function fetchAPI() {
    getOrderAPI({
      params: {
        OnlyRefundData: 1,
        TransactionID: props.transactionId,
      },
    })
      .then((res) => {
        if (res.data.Code === 200) {
          data.value = res.data.Response;
          selectedItemReason.value.length = data.value.Markets.length;
          selectedItemOtherReason.value.length = data.value.Markets.length;
          selectedItemType2Reason.value.length = data.value.Markets.length;
          data.value.Markets.forEach((el, index) => {
            el.openSelect = false;
            el.refundCount = 1;
            el.checked = false;
            el.reasonSelect = [];

            // 正常商品才會有行銷活動，加價購不會有
            // 行銷活動退貨邏輯CanScatteredRefund是false就必須一起退
            if (el.Events.length && el.Events[0].CanScatteredRefund === false) {
              el.selectCount = el.Count;
            } else if (el.Type === 3) {
              // 加價購退貨必須全退
              el.selectCount = el.Count;
            } else {
              el.selectCount = `${el.ID}_1`;
            }

            // 商品數量
            const countOptions = [];

            for (let i = 1; i < el.Count + 1; i++) {
              countOptions.push({
                value: i,
                text: i.toString(),
              });
            }

            el.countOptions = countOptions;

            // 給予每一項商品預設空字串理由
            selectedItemReason.value[index] = '';
            selectedItemOtherReason.value[index] = '';
            selectedItemType2Reason.value[index] = '';
          });
          dataIsLoad.value = true;
        } else {
          closeModal();
        }
      })
      .catch((error) => {
        console.log(error);
        closeModal();
      });
  }

  function toggleOption(index) {
    data.value.Markets[index].openSelect = !data.value.Markets[index].openSelect;
  }

  function changeValue(newValue, index) {
    data.value.Markets[index].openSelect = false;
    data.value.Markets[index].refundCount = newValue;
  }

  function onMinus(index) {
    if (data.value.Markets[index].refundCount > 1) {
      data.value.Markets[index].refundCount -= 1;
    }
  }

  function onPlus(index) {
    if (data.value.Markets[index].refundCount < data.value.Markets[index].Count) {
      data.value.Markets[index].refundCount += 1;
    }
  }

  /**
   * @description 清空退貨原因的多選框
   */
  function clearReasonCheckbox() {
    reasonCheck.value.forEach((category) => {
      category.check = false;
      category.sub.forEach((sub) => {
        sub.check = false;
      });
    });
  }

  function setReasonCheckbox() {
    clearReasonCheckbox();

    if (selectedItemDetail.value.reasonSelect.length === 0) return;

    selectedItemDetail.value.reasonSelect.forEach((code) => {
      reasonCheck.value.forEach((category) => {
        if (category.code === code.substring(0, 1)) {
          category.check = true;
          category.sub.forEach((sub) => {
            if (sub.code === code.substring(1)) {
              sub.check = true;
            }
          });
        }
      });
    });
  }

  /**
   * @description 勾選退貨商品
   * @param {Object} item 退貨商品
   */
  function toggleChecked(item) {
    item.checked = !item.checked;
    getSelectedItem();
  }

  /**
   * @description 大項目種類 code
   * @param {String} categoryCode
   */
  function toggleReasonCategory(categoryCode) {
    reasonCheck.value.forEach((ele) => {
      if (ele.code === categoryCode) {
        ele.check = !ele.check;
        // 大項目種類取消後小項目也必需取消

        if (ele.check === false) {
          ele.sub.forEach((subCode) => {
            subCode.check = false;
            const reasonCodeIndex = selectedItemDetail.value.reasonSelect.indexOf(
              ele.code + subCode.code,
            );
            if (reasonCodeIndex !== -1) {
              selectedItemDetail.value.reasonSelect.splice(reasonCodeIndex, 1);
            }
          });
          if (ele.code === 'b') {
            selectedItemType2Reason.value[fillReasonIndex.value] = '';
          }
        }
      }
    });
  }

  /**
   * @description 紀錄子項目勾選狀態
   * @param {number} categoryIndex
   * @param {number} subCategoryIndex
   */
  function toggleReasonSubCategory(categoryIndex, subCategoryIndex) {
    reasonCheck.value[categoryIndex].sub[subCategoryIndex].check =
      !reasonCheck.value[categoryIndex].sub[subCategoryIndex].check;

    const reasonCode =
      reasonCheck.value[categoryIndex].code +
      reasonCheck.value[categoryIndex].sub[subCategoryIndex].code;

    const findIndex = data.value.Markets.findIndex(
      (element) => element.ID === selectedItemDetail.value.ID,
    );

    const reasonCodeIndex = selectedItemDetail.value.reasonSelect.indexOf(reasonCode);
    if (
      selectedItemReason.value[findIndex].length !== 0 &&
      selectedItemReason.value[findIndex].at(-1) !== ','
    ) {
      selectedItemReason.value[findIndex] += ',';
    }
    if (reasonCodeIndex === -1) {
      selectedItemDetail.value.reasonSelect.push(reasonCode);
    } else {
      selectedItemDetail.value.reasonSelect.splice(reasonCodeIndex, 1);
    }
  }

  /**
   * @description 取得勾選的退貨商品
   */
  function getSelectedItem() {
    selectedItem.value = [];
    data.value.Markets.forEach((element) => {
      if (element.checked) {
        selectedItem.value.push({
          OrderMarketID: element.ID,
          Count: element.refundCount.toString(),
        });
      }
    });
  }

  /**
   * @description 前往勾選退貨原因
   */
  function goStep2() {
    step.value = 2;

    selectedItemDetail.value = data.value.Markets.find(
      (element) => element.ID == selectedItem.value['0'].OrderMarketID,
    );

    // mapping selectedItemReason array index
    fillReasonIndex.value = data.value.Markets.findIndex(
      (element) => element.ID === selectedItemDetail.value.ID,
    );

    setReasonCheckbox();
  }

  /**
   * @description 勾選多個退貨商品時
   * @param {Number} dir 上一個/下一個退貨商品
   */
  function loopStep2(dir) {
    let selectItemIndex = selectedItem.value.findIndex(
      (item) => item.OrderMarketID === selectedItemDetail.value.ID,
    );
    // 下一個退貨商品
    if (dir === 1) {
      selectItemIndex += 1;
    }

    // 上一個退貨商品
    if (dir === -1) {
      // 當只有選一個商品退貨時，或有多項商品退貨但不是從列表中第一項商品退時
      if (selectItemIndex === 0 && step.value !== 3) {
        step.value = 1;
        clearReasonCheckbox();
        return;
      }

      // 贈品畫面時
      if (step.value === 3) {
        step.value = 2;
      } else {
        // 返回上一個退貨商品
        selectItemIndex -= 1;
      }
      selectedItemReason.value[selectItemIndex] = '';
    }

    // 上一個 or 下一個的商品 detail
    selectedItemDetail.value = data.value.Markets.find(
      (element) => element.ID == selectedItem.value[selectItemIndex].OrderMarketID,
    );

    fillReasonIndex.value = data.value.Markets.findIndex(
      (element) => element.ID === selectedItemDetail.value.ID,
    );

    setReasonCheckbox();
  }

  /**
   * @description 寫入勾選框及輸入框內容
   * @param {Number} index
   */
  function setOtherReason() {
    selectedItem.value.forEach((item) => {
      const refundItem = data.value.Markets.find((e) => e.ID === item.OrderMarketID);
      const refundItemIndex = data.value.Markets.findIndex((e) => e.ID === item.OrderMarketID);
      refundItem.reasonSelect.forEach((select) => {
        const categoryCode = select.substring(0, 1);
        const subCode = select.substring(1);
        const categoryObj = reasonCheck.value.find((e) => e.code === categoryCode);
        const subObj = categoryObj.sub.find((e) => e.code === subCode);
        selectedItemReason.value[refundItemIndex] +=
          categoryObj.text.substring(0, categoryObj.text.indexOf('、')) + '_' + subObj.text + ',';
      });
      // 寫入五、其他(選填)
      if (selectedItemOtherReason.value[refundItemIndex].length !== 0) {
        selectedItemReason.value[refundItemIndex] +=
          '五_' + selectedItemOtherReason.value[refundItemIndex].replace(/,/g, '，') + ',';
      }
      // 寫入二的備註說明
      const string2Array = selectedItemReason.value[refundItemIndex].split(',');
      if (selectedItemType2Reason.value[refundItemIndex].length !== 0) {
        string2Array.splice(
          string2Array.findLastIndex((e) => e.includes('二')) + 1,
          0,
          '二_' + selectedItemType2Reason.value[refundItemIndex].replace(/,/g, '，'),
        );
      }
      selectedItemReason.value[refundItemIndex] = string2Array.toString();
    });
  }

  /**
   * @description 返回第一步
   */
  function returnStep1() {
    step.value = 1;
    clearReasonCheckbox();
  }

  /**
   * @description 退貨試算
   */
  async function getReservedMarkets(orderID) {
    getSelectedItem();
    setOtherReason();

    selectedItemReason.value.forEach((itemReason, index) => {
      if (itemReason.substr(-1) === ',') {
        selectedItemReason.value[index] = itemReason.slice(0, -1);
      }
    });

    postRefundCalculateAPI({
      TransactionID: orderID,
      RefundMarkets: selectedItem.value,
    })
      .then((res) => {
        if (res.data.Code === 200) {
          reservedMarkets.value = res.data.Response.ReservedMarkets;
          checkRemainItemIfMatchAddon();
        } else {
          closeModal();
        }
      })
      .catch((error) => {
        console.log(error);
        closeModal();
      });
  }

  /**
   * @description 檢查剩下商品是否有加價購且是否符合條件, 若無跳警告視窗提醒
   */
  function checkRemainItemIfMatchAddon() {
    reservedUnMatchAddons.value = [];
    if (reservedMarkets.value.length) {
      reservedMarkets.value.forEach((remainItem) => {
        if (remainItem.Type === 3 && !remainItem.AddOnEvent.IsMatch) {
          reservedUnMatchAddons.value.push(remainItem);
        }
      });
    }

    if (reservedUnMatchAddons.value.length > 0) {
      let reservedText = '';
      reservedUnMatchAddons.value.forEach((ele, index) => {
        reservedText += `<p class="mb-1">${index + 1}. ${ele.Name}(${ele.ColorName}${
          ele.Size
        })將以NT$${ele.SellPrice.toLocaleString()}計算</p>`;
      });

      alertDialog.special(
        `
        <div class="text-left">
          提醒您:<br />您選擇的退貨商品無達到加價購活動門檻<br />
          <span class="text-danger h5">請您勾選是否要將加價購商品一併退回</span><br />
          若無退回 加價商品將會以商品原價計算 :
        </div>
        <div class="text-left text-danger my-4">${reservedText}</div>
        <div class="row">
          <div class="col">
            <a
              id=""
              class="btn-confirm-alert btn btn-outline-secondary btn-block"
              href="#"
              >不退回，繼續下一步</a
            >
          </div>
          <div class="col">
            <a id="" class="btn-cancel-alert btn btn-secondary btn-block" href="#"
              >我要退回</a
            >
          </div>
        </div>
      `,
        () => {
          checkRefundGift(reservedMarkets.value);
        },
      );
    } else {
      checkRefundGift(reservedMarkets.value);
    }
  }

  /**
   * @description 確認是否有贈品
   * @param {Array} remainItems 退貨商品
   */
  function checkRefundGift(remainItems) {
    const giftItems = remainItems.filter((item) => item.Type === 2 && !item.GiftEvent.IsMatch);
    isCheckGift.value = true;
    giftData.value = giftItems;
    if (giftData.value.length === 0) {
      refundForm.value.submit();
    } else {
      step.value = 3;
    }
  }

  /**
   * @description 取得商品退貨理由
   * @param {Object} item 退貨商品
   */
  function getSelectedItemReason(item) {
    const findIndex = data.value.Markets.findIndex((element) => element.ID === item.OrderMarketID);

    return selectedItemReason.value[findIndex];
  }

  function submitFn() {
    refundForm.value.submit();
  }

  return {
    step,
    data,
    selectedItem,
    selectedItemReason,
    selectedItemType2Reason,
    selectedItemOtherReason,
    dataIsLoad,
    isCheckGift,
    giftData,
    reservedMarkets,
    reservedUnMatchAddons,
    fillReasonIndex,
    selectedItemDetail,
    reasonCheck,
    refundForm,
    type2ReasonOverMax,
    type5ReasonOverMax,
    showNextStep,
    canMoveNextItem,
    upToTwoSub,
    submitFn,
    toggleOption,
    changeValue,
    clearReasonCheckbox,
    setReasonCheckbox,
    toggleChecked,
    toggleReasonCategory,
    toggleReasonSubCategory,
    getSelectedItem,
    goStep2,
    loopStep2,
    setOtherReason,
    returnStep1,
    getReservedMarkets,
    checkRemainItemIfMatchAddon,
    checkRefundGift,
    getSelectedItemReason,
    onMinus,
    onPlus,
    fetchAPI,
  };
}

export default useRefundList;
